import React from 'react';
import { Box, Typography, Stack, Divider, alpha, useTheme } from '@mui/material';
import TrendingUpIcon from '@mui/icons-material/TrendingUp';
import TrendingDownIcon from '@mui/icons-material/TrendingDown';
import AccountBalanceWalletIcon from '@mui/icons-material/AccountBalanceWallet';
import PaymentsIcon from '@mui/icons-material/Payments';

interface TransactionSummaryProps {
    state: {
        yesterdaybalance: number;
        todaySellAmount: number;
        todayAmountPaid: number;
        todayDue: number;
    };
    totalExpense: () => number;
    finalClosing: () => number;
}

const SummaryRow = ({
    label,
    value,
    icon,
    color,
    highlight,
}: {
    label: string;
    value: number | string;
    icon?: React.ReactNode;
    color?: string;
    highlight?: boolean;
}) => {
    const theme = useTheme();
    return (
        <Box
            sx={{
                display: 'flex',
                alignItems: 'center',
                justifyContent: 'space-between',
                px: 2,
                py: highlight ? 1.5 : 1.1,
                borderRadius: 1.5,
                bgcolor: highlight ? alpha(color || theme.palette.primary.main, 0.08) : 'transparent',
                border: highlight ? `1px solid ${alpha(color || theme.palette.primary.main, 0.2)}` : '1px solid transparent',
            }}
        >
            <Stack direction="row" spacing={1} alignItems="center">
                {icon && <Box sx={{ color: color || 'text.secondary', display: 'flex' }}>{icon}</Box>}
                <Typography
                    variant={highlight ? 'subtitle2' : 'body2'}
                    fontWeight={highlight ? 700 : 500}
                    color={highlight ? color || 'text.primary' : 'text.secondary'}
                >
                    {label}
                </Typography>
            </Stack>
            <Typography
                variant={highlight ? 'subtitle1' : 'body2'}
                fontWeight={highlight ? 800 : 600}
                sx={{ color: color || 'text.primary' }}
            >
                ₹{value ?? 0}
            </Typography>
        </Box>
    );
};

export const TransactionSummary = ({ state, totalExpense, finalClosing }: TransactionSummaryProps) => {
    const theme = useTheme();
    const closing = finalClosing();

    return (
        <Box>
            <Typography
                variant="subtitle2"
                color="text.secondary"
                sx={{ mb: 2, fontWeight: 600, letterSpacing: 0.5, textTransform: 'uppercase', fontSize: '0.7rem' }}
            >
                Today's Summary
            </Typography>

            <Stack spacing={0.75}>
                <SummaryRow
                    label="Yesterday Balance"
                    value={state.yesterdaybalance}
                    icon={<AccountBalanceWalletIcon sx={{ fontSize: 16 }} />}
                    color={theme.palette.info.main}
                    highlight
                />
                <SummaryRow
                    label="Today's Sale"
                    value={state.todaySellAmount}
                    icon={<TrendingUpIcon sx={{ fontSize: 16 }} />}
                    color={theme.palette.success.main}
                />
                <SummaryRow
                    label="Cash Paid Today"
                    value={state.todayAmountPaid}
                    icon={<PaymentsIcon sx={{ fontSize: 16 }} />}
                    color={theme.palette.primary.main}
                />
                <SummaryRow
                    label="Today's Due"
                    value={state.todayDue}
                    icon={<TrendingDownIcon sx={{ fontSize: 16 }} />}
                    color={theme.palette.warning.main}
                />
                <SummaryRow
                    label="Total Expenses"
                    value={totalExpense()}
                    icon={<TrendingDownIcon sx={{ fontSize: 16 }} />}
                    color={theme.palette.error.main}
                />

                <Divider sx={{ my: 0.5 }} />

                <SummaryRow
                    label="Final Closing Balance"
                    value={closing}
                    icon={<AccountBalanceWalletIcon sx={{ fontSize: 18 }} />}
                    color={closing >= 0 ? theme.palette.success.dark : theme.palette.error.dark}
                    highlight
                />
            </Stack>
        </Box>
    );
};
